import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import Loader from './Loader';
import './Product.css';
import { ADD_PRODUCT_TO_WISHLIST, GET_CUSTOMER_WISHLIST } from '../queries/customerQueries';

const GET_PRODUCT_DETAILS = gql`
    query getProductDetails($urlKey: String!) {
        products(filter: {url_key: {eq: $urlKey}}) {
            items {
                id
                uid
                sku
                name
                __typename
                description {
                    html
                }
                media_gallery {
                    url
                    label
                }
                price_range {
                    minimum_price {
                        final_price {
                            currency
                            value
                        }
                        regular_price {
                            currency
                            value
                        }
                        discount {
                            amount_off
                            percent_off
                        }
                    }
                }
                stock_status
                only_x_left_in_stock
                categories {
                    id
                    name
                    url_path
                    url_suffix
                    breadcrumbs {
                        category_id
                        category_name
                        category_url_path
                    }
                }
                reviews {
                    items {
                        average_rating
                        ratings_breakdown {
                            name
                            value
                        }
                        nickname
                        summary
                        text
                        created_at
                    }
                }
                rating_summary
                review_count
                ... on ConfigurableProduct {
                    configurable_options {
                        id
                        attribute_id
                        label
                        position
                        use_default
                        attribute_code
                        values {
                            value_index
                            label
                            store_label
                            use_default_value
                            swatch_data {
                                value
                            }
                        }
                    }
                    variants {
                        product {
                            id
                            sku
                            name
                            stock_status
                            price_range {
                                minimum_price {
                                    final_price {
                                        currency
                                        value
                                    }
                                }
                            }
                        }
                        attributes {
                            code
                            value_index
                            label
                        }
                    }
                }
            }
        }
    }
`;

const GET_REVIEW_METADATA = gql`
    query getProductReviewRatingsMetadata {
        productReviewRatingsMetadata {
            items {
                id
                name
                values {
                    value_id
                    value
                }
            }
        }
    }
`;

const CREATE_PRODUCT_REVIEW = gql`
    mutation createProductReview($input: CreateProductReviewInput!) {
        createProductReview(input: $input) {
            review {
                nickname
                summary
                text
                average_rating
                ratings_breakdown {
                    name
                    value
                }
            }
        }
    }
`;

const Product = ({ urlKey: urlKeyProp }) => {
  const { urlKey: urlKeyParam } = useParams();
  const urlKey = urlKeyProp || urlKeyParam;
  const location = useLocation();
  const { addToCart, loading: cartLoading } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();
  const { updateBreadcrumbs } = useBreadcrumb();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [wishlistMessage, setWishlistMessage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('details');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewForm, setReviewForm] = useState({
    nickname: '',
    rating: 5,
    summary: '',
    text: ''
  });

  const { loading, error, data, refetch } = useQuery(GET_PRODUCT_DETAILS, {
    variables: { urlKey }
  });

  const { data: wishlistData } = useQuery(GET_CUSTOMER_WISHLIST, {
    skip: !isAuthenticated
  });

  const [addProductToWishlist, { loading: wishlistLoading }] = useMutation(ADD_PRODUCT_TO_WISHLIST);

  const { data: reviewMetadata } = useQuery(GET_REVIEW_METADATA);

  const [createReview, { loading: reviewLoading }] = useMutation(CREATE_PRODUCT_REVIEW);

  const product = data?.products?.items?.[0];
  const isConfigurable = product?.__typename === 'ConfigurableProduct';
  const reviews = product?.reviews?.items || [];
  const reviewCount = product?.review_count || 0;
  const ratingSummary = product?.rating_summary || 0;
  const ratingMetadata = reviewMetadata?.productReviewRatingsMetadata?.items || [];

  // Set breadcrumbs from product categories data
  useEffect(() => {
    if (product && product.name) {
      let crumbs = [{ label: 'Home', path: '/' }];

      // Try to get breadcrumbs from product categories
      if (product.categories && product.categories.length > 0) {
        // Find a category with breadcrumbs (prefer the one with the most breadcrumbs)
        const categoryWithBreadcrumbs = product.categories
          .filter(cat => cat.breadcrumbs && cat.breadcrumbs.length > 0)
          .sort((a, b) => (b.breadcrumbs?.length || 0) - (a.breadcrumbs?.length || 0))[0];

        if (categoryWithBreadcrumbs && categoryWithBreadcrumbs.breadcrumbs) {
          // Add breadcrumbs from the category
          categoryWithBreadcrumbs.breadcrumbs.forEach(crumb => {
            crumbs.push({
              label: crumb.category_name,
              path: `/${crumb.category_url_path}.html`
            });
          });

          // Add the current category
          crumbs.push({
            label: categoryWithBreadcrumbs.name,
            path: `/${categoryWithBreadcrumbs.url_path}${categoryWithBreadcrumbs.url_suffix || ''}`
          });
        }
      }

      // Add the product as the final breadcrumb
      crumbs.push({ label: product.name, path: location.pathname });

      updateBreadcrumbs(crumbs);
    }
  }, [product?.name, product?.categories, location.pathname, updateBreadcrumbs]);

  // Find matching variant based on selected options
  const findMatchingVariant = (options) => {
    if (!isConfigurable || !product?.variants) return null;

    return product.variants.find(variant => {
      return variant.attributes.every(attr =>
        options[attr.code] === attr.value_index
      );
    });
  };

  // Update selected variant when options change
  useEffect(() => {
    if (isConfigurable && product) {
      const variant = findMatchingVariant(selectedOptions);
      setSelectedVariant(variant);
    }
  }, [selectedOptions, isConfigurable, product]);

  if (loading) return <Loader fullScreen />;
  if (error) return <div>Error: {error.message}</div>;

  if (!product) {
    return <div>Product not found</div>;
  }

  // Get current price (from variant if selected, otherwise base product)
  const currentProduct = selectedVariant?.product || product;
  const price = currentProduct.price_range?.minimum_price?.final_price;
  const regularPrice = currentProduct.price_range?.minimum_price?.regular_price;
  const discount = currentProduct.price_range?.minimum_price?.discount;

  const handleOptionChange = (attributeCode, valueIndex) => {
    setSelectedOptions(prev => ({
      ...prev,
      [attributeCode]: parseInt(valueIndex)
    }));
  };

  const canAddToCart = () => {
    if (!isConfigurable) return true;

    // Check if all required options are selected
    return product.configurable_options?.every(option =>
      selectedOptions[option.attribute_code] !== undefined
    );
  };

  const handleAddToCart = () => {
    if (!canAddToCart()) return;

    if (isConfigurable && selectedVariant) {
      addToCart(selectedVariant.product.sku, quantity);
    } else {
      addToCart(product.sku, quantity);
    }
  };

  const handleQuantityChange = (value) => {
    const numValue = parseInt(value);
    if (numValue > 0 && numValue <= 9999) {
      setQuantity(numValue);
    }
  };

  const incrementQuantity = () => {
    if (quantity < 9999) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setReviewMessage('Please log in to write a review.');
      setTimeout(() => {
        setShowReviewModal(false);
        openLoginModal();
      }, 2000);
      return;
    }

    try {
      // Build ratings array from metadata
      let ratingsArray = [];

      if (ratingMetadata.length > 0) {
        // For each rating type (Quality, Price, Value), find the corresponding value_id
        ratingMetadata.forEach(ratingType => {
          // Find the value that matches the user's rating (1-5 stars)
          const ratingValue = ratingType.values.find(v => parseInt(v.value) === reviewForm.rating);

          if (ratingValue) {
            ratingsArray.push({
              id: ratingType.id,
              value_id: ratingValue.value_id
            });
          }
        });
      }

      // If no metadata found, use a simple approach with base64 encoding
      if (ratingsArray.length === 0) {
        // Most Magento installations have rating ID 1, 2, 3 with value IDs from 1-15
        // Rating ID 1 (Quality) typically has values 1-5, ID 2 (Value) has 6-10, ID 3 (Price) has 11-15
        const ratingId = btoa("1"); // Base64 encode rating ID
        const valueId = btoa(reviewForm.rating.toString()); // Base64 encode the value

        ratingsArray.push({
          id: ratingId,
          value_id: valueId
        });
      }

      console.log('Submitting review with ratings:', ratingsArray, 'Original rating:', reviewForm.rating);

      await createReview({
        variables: {
          input: {
            sku: product.sku,
            nickname: reviewForm.nickname,
            summary: reviewForm.summary,
            text: reviewForm.text,
            ratings: ratingsArray
          }
        }
      });

      setReviewMessage('Thank you for your review! It will be visible after approval.');
      setReviewForm({
        nickname: '',
        rating: 5,
        summary: '',
        text: ''
      });

      // Refetch product to get updated reviews
      setTimeout(() => {
        refetch();
        setShowReviewModal(false);
        setReviewMessage('');
      }, 2000);

    } catch (error) {
      console.error('Error submitting review:', error);

      // Extract more user-friendly error message
      let errorMessage = 'Failed to submit review. Please try again.';

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setReviewMessage(errorMessage);
    }
  };

  const handleReviewFormChange = (field, value) => {
    setReviewForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWriteReviewClick = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    setShowReviewModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleAddToWishlist = async () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    try {
      const wishlistId = wishlistData?.customer?.wishlists?.[0]?.id;

      if (!wishlistId) {
        setWishlistMessage('Wishlist not found. Please try again.');
        setTimeout(() => setWishlistMessage(''), 3000);
        return;
      }

      await addProductToWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItems: [{
            sku: product.sku,
            quantity: 1
          }]
        },
        refetchQueries: [{ query: GET_CUSTOMER_WISHLIST }]
      });

      setWishlistMessage('Product added to wishlist!');
      setTimeout(() => setWishlistMessage(''), 3000);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      setWishlistMessage(error.message || 'Failed to add to wishlist');
      setTimeout(() => setWishlistMessage(''), 5000);
    }
  };

  return (
    <div className="product-detail">
      <div className="product-detail-content">
        <div>
          <img
            src={product.media_gallery?.[0]?.url || '/placeholder.jpg'}
            alt={product.name}
            className="product-detail-image"
          />
        </div>

        <div className="product-detail-info">
          <h1>{product.name}</h1>

          {reviewCount > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div className="product-rating">
                <div className="product-stars" style={{ fontSize: '1.25rem' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={star <= Math.round(ratingSummary / 20) ? 'star-filled' : 'star-empty'}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="review-count" style={{ fontSize: '0.95rem' }}>
                  {(ratingSummary / 20).toFixed(1)} out of 5 ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
          )}

          <div className="product-detail-price">
            {discount?.amount_off > 0 && (
              <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '10px' }}>
                ${regularPrice?.value}
              </span>
            )}
            ${price?.value}
            {discount?.percent_off > 0 && (
              <span style={{ color: '#e53e3e', marginLeft: '10px' }}>
                -{discount.percent_off}%
              </span>
            )}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <strong>SKU:</strong> {product.sku}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <strong>Stock Status:</strong> {currentProduct.stock_status || product.stock_status}
            {product.only_x_left_in_stock && (
              <span style={{ color: '#e53e3e', marginLeft: '10px' }}>
                Only {product.only_x_left_in_stock} left!
              </span>
            )}
          </div>

          {/* Configurable Options */}
          {isConfigurable && product.configurable_options && (
            <div style={{ marginBottom: '1.5rem' }}>
              {product.configurable_options.map((option) => (
                <div key={option.id} style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    fontSize: '0.875rem'
                  }}>
                    {option.label}:
                  </label>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    {option.values.map((value) => {
                      const isSelected = selectedOptions[option.attribute_code] === value.value_index;
                      return (
                        <button
                          key={value.value_index}
                          onClick={() => handleOptionChange(option.attribute_code, value.value_index)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: `2px solid ${isSelected ? '#7c3aed' : '#d1d5db'}`,
                            borderRadius: '6px',
                            backgroundColor: isSelected ? '#7c3aed' : 'white',
                            color: isSelected ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            minWidth: '60px'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.target.style.borderColor = '#7c3aed';
                              e.target.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.target.style.borderColor = '#d1d5db';
                              e.target.style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {value.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {wishlistMessage && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              backgroundColor: wishlistMessage.includes('Failed') ? '#fee2e2' : '#d1fae5',
              color: wishlistMessage.includes('Failed') ? '#991b1b' : '#065f46',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}>
              {wishlistMessage}
            </div>
          )}

          {/* Quantity Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Quantity:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={decrementQuantity}
                disabled={quantity <= 1}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  cursor: quantity > 1 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (quantity > 1) {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.color = '#7c3aed';
                  }
                }}
                onMouseLeave={(e) => {
                  if (quantity > 1) {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }
                }}
              >
                -
              </button>
              <input
                type="number"
                min="1"
                max="9999"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                className="qty-input"
                style={{
                  width: '80px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  textAlign: 'center',
                  fontSize: '1rem',
                  fontWeight: '500',
                  padding: '0'
                }}
              />
              <button
                onClick={incrementQuantity}
                disabled={quantity >= 9999}
                style={{
                  width: '40px',
                  height: '40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  cursor: quantity < 9999 ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (quantity < 9999) {
                    e.target.style.borderColor = '#7c3aed';
                    e.target.style.color = '#7c3aed';
                  }
                }}
                onMouseLeave={(e) => {
                  if (quantity < 9999) {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.color = '#374151';
                  }
                }}
              >
                +
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <button
              onClick={handleAddToCart}
              disabled={cartLoading || currentProduct.stock_status !== 'IN_STOCK' || !canAddToCart()}
              style={{
                flex: 1,
                padding: '0.75rem 2rem',
                backgroundColor: (currentProduct.stock_status === 'IN_STOCK' && canAddToCart()) ? '#7c3aed' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: (currentProduct.stock_status === 'IN_STOCK' && canAddToCart()) ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                if (currentProduct.stock_status === 'IN_STOCK' && canAddToCart()) {
                  e.target.style.backgroundColor = '#6d28d9';
                }
              }}
              onMouseLeave={(e) => {
                if (currentProduct.stock_status === 'IN_STOCK' && canAddToCart()) {
                  e.target.style.backgroundColor = '#7c3aed';
                }
              }}
            >
              {cartLoading ? 'Adding...' :
               !canAddToCart() ? 'Please select options' :
               'Add to Cart'}
            </button>

            <button
              onClick={handleAddToWishlist}
              disabled={wishlistLoading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'white',
                color: '#7c3aed',
                border: '2px solid #7c3aed',
                borderRadius: '6px',
                fontSize: '1.5rem',
                fontWeight: '600',
                cursor: wishlistLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '60px',
                lineHeight: '1'
              }}
              onMouseEnter={(e) => {
                if (!wishlistLoading) {
                  e.target.style.backgroundColor = '#7c3aed';
                  e.target.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!wishlistLoading) {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#7c3aed';
                }
              }}
              title="Add to Wishlist"
            >
              {wishlistLoading ? '...' : '♥'}
            </button>
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      <div className="product-tabs">
        <div className="tab-headers">
          <button
            className={`tab-header ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Product Details
          </button>
          <button
            className={`tab-header ${activeTab === 'more-info' ? 'active' : ''}`}
            onClick={() => setActiveTab('more-info')}
          >
            More Information
          </button>
          <button
            className={`tab-header ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            Reviews
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'details' && (
            <div className="tab-panel">
              {product.description?.html ? (
                <div dangerouslySetInnerHTML={{ __html: product.description.html }} />
              ) : (
                <p>No product details available.</p>
              )}
            </div>
          )}

          {activeTab === 'more-info' && (
            <div className="tab-panel">
              <table className="product-attributes">
                <tbody>
                  <tr>
                    <th>SKU</th>
                    <td>{product.sku}</td>
                  </tr>
                  <tr>
                    <th>Stock Status</th>
                    <td>{product.stock_status}</td>
                  </tr>
                  {product.only_x_left_in_stock && (
                    <tr>
                      <th>Available Quantity</th>
                      <td>{product.only_x_left_in_stock}</td>
                    </tr>
                  )}
                  {isConfigurable && product.configurable_options && (
                    product.configurable_options.map((option) => (
                      <tr key={option.id}>
                        <th>{option.label}</th>
                        <td>{option.values.map(v => v.label).join(', ')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="tab-panel">
              <div className="reviews-section">
                <div className="reviews-header">
                  <div>
                    <h3>Customer Reviews</h3>
                    {reviewCount > 0 && (
                      <div className="rating-summary">
                        <div className="stars-display">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              className={star <= Math.round(ratingSummary / 20) ? 'star-filled' : 'star-empty'}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="rating-text">
                          {(ratingSummary / 20).toFixed(1)} out of 5 ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleWriteReviewClick}
                    className="write-review-btn"
                  >
                    Write a Review
                  </button>
                </div>

                {reviewCount === 0 ? (
                  <p className="no-reviews">No reviews yet. Be the first to review this product!</p>
                ) : (
                  <div className="reviews-list">
                    {reviews.map((review, index) => (
                      <div key={index} className="review-item">
                        <div className="review-header-item">
                          <div>
                            <div className="review-stars">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                  key={star}
                                  className={star <= review.average_rating ? 'star-filled' : 'star-empty'}
                                >
                                  ★
                                </span>
                              ))}
                            </div>
                            <p className="review-author">
                              {review.nickname} • {formatDate(review.created_at)}
                            </p>
                          </div>
                        </div>
                        <h4 className="review-summary">{review.summary}</h4>
                        <p className="review-text">{review.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2>Write a Review</h2>
              <button
                className="close-btn"
                onClick={() => setShowReviewModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleReviewSubmit} className="review-form">
              {reviewMessage && (
                <div className={`review-message ${reviewMessage.includes('Failed') || reviewMessage.includes('log in') ? 'error' : 'success'}`}>
                  {reviewMessage}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="nickname">Nickname *</label>
                <input
                  type="text"
                  id="nickname"
                  value={reviewForm.nickname}
                  onChange={(e) => handleReviewFormChange('nickname', e.target.value)}
                  required
                  placeholder="Enter your nickname"
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="rating">Rating *</label>
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star ${star <= reviewForm.rating ? 'filled' : ''}`}
                      onClick={() => handleReviewFormChange('rating', star)}
                      disabled={reviewLoading}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="summary">Summary *</label>
                <input
                  type="text"
                  id="summary"
                  value={reviewForm.summary}
                  onChange={(e) => handleReviewFormChange('summary', e.target.value)}
                  required
                  placeholder="Brief summary of your review"
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="text">Review *</label>
                <textarea
                  id="text"
                  value={reviewForm.text}
                  onChange={(e) => handleReviewFormChange('text', e.target.value)}
                  required
                  rows="5"
                  placeholder="Write your detailed review here..."
                  disabled={reviewLoading}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowReviewModal(false)}
                  disabled={reviewLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={reviewLoading}
                >
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;